import React, { useState, useEffect } from 'react';
import {
  getAllServices,
  createService,
  updateService,
  deleteService,
} from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Switch } from '../../components/ui/switch';
import {
  Smartphone,
  Plus,
  MoreVertical,
  Trash2,
  Edit,
  Power,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ServicesManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    base_price: '',
    category: 'recarga',
    is_active: true,
  });

  const fetchServices = async () => {
    try {
      const res = await getAllServices();
      setServices(res.data);
    } catch (err) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      base_price: '',
      category: 'recarga',
      is_active: true,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.base_price) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSubmitting(true);
    try {
      await createService({
        ...form,
        base_price: parseFloat(form.base_price),
      });
      toast.success('Servicio creado');
      setCreateOpen(false);
      resetForm();
      fetchServices();
    } catch (err) {
      toast.error('Error al crear servicio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await updateService(selectedService.id, {
        ...form,
        base_price: parseFloat(form.base_price),
      });
      toast.success('Servicio actualizado');
      setEditOpen(false);
      fetchServices();
    } catch (err) {
      toast.error('Error al actualizar servicio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await updateService(service.id, { is_active: !service.is_active });
      toast.success(
        service.is_active ? 'Servicio desactivado' : 'Servicio activado'
      );
      fetchServices();
    } catch (err) {
      toast.error('Error al actualizar servicio');
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`¿Eliminar servicio "${service.name}"?`)) return;
    try {
      await deleteService(service.id);
      toast.success('Servicio eliminado');
      fetchServices();
    } catch (err) {
      toast.error('Error al eliminar servicio');
    }
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setForm({
      name: service.name,
      description: service.description,
      base_price: service.base_price.toString(),
      category: service.category,
      is_active: service.is_active,
    });
    setEditOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="services-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Servicios</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los servicios de recarga
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button
              className="gap-2 btn-glow"
              onClick={resetForm}
              data-testid="create-service-btn"
            >
              <Plus className="w-4 h-4" />
              Nuevo Servicio
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Crear Servicio</DialogTitle>
              <DialogDescription>
                Agrega un nuevo servicio de recarga
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del servicio</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-secondary/50"
                  placeholder="Ej: Recarga $10"
                  required
                  data-testid="service-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className="bg-secondary/50"
                  placeholder="Descripción del servicio"
                  data-testid="service-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Precio Base ($)</Label>
                <Input
                  id="price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.base_price}
                  onChange={(e) =>
                    setForm({ ...form, base_price: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="service-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Categoría</Label>
                <Input
                  id="category"
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  className="bg-secondary/50"
                  placeholder="Ej: recarga, datos, minutos"
                  data-testid="service-category-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="active">Activo</Label>
                <Switch
                  id="active"
                  checked={form.is_active}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, is_active: checked })
                  }
                  data-testid="service-active-switch"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-create-service"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Crear Servicio
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border/40">
        <CardContent className="p-0">
          {services.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay servicios registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead>Servicio</TableHead>
                  <TableHead>CUP</TableHead>
                  <TableHead>Costo DirectCodes</TableHead>
                  <TableHead>Precio Venta</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => (
                  <TableRow
                    key={service.id}
                    className="border-border/30 hover:bg-secondary/30"
                    data-testid={`service-row-${service.id}`}
                  >
                    <TableCell className="font-medium">{service.name}</TableCell>
                    <TableCell>
                      <span className="font-mono font-bold">
                        {service.cup_amount || '-'} CUP
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-muted-foreground">
                        {formatCurrency(service.cost_price || 0)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-accent font-bold">
                        {formatCurrency(service.base_price)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          service.is_active ? 'status-active' : 'status-inactive'
                        }
                      >
                        {service.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            data-testid={`service-menu-${service.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-card border-border"
                        >
                          <DropdownMenuItem
                            onClick={() => openEditDialog(service)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleActive(service)}
                          >
                            <Power className="w-4 h-4 mr-2" />
                            {service.is_active ? 'Desactivar' : 'Activar'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(service)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Editar Servicio</DialogTitle>
            <DialogDescription>
              Actualizar información del servicio
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nombre del servicio</Label>
              <Input
                id="edit-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-secondary/50"
                required
                data-testid="edit-service-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Descripción</Label>
              <Textarea
                id="edit-description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="bg-secondary/50"
                data-testid="edit-service-description"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-price">Precio Base ($)</Label>
              <Input
                id="edit-price"
                type="number"
                min="0.01"
                step="0.01"
                value={form.base_price}
                onChange={(e) =>
                  setForm({ ...form, base_price: e.target.value })
                }
                className="bg-secondary/50"
                required
                data-testid="edit-service-price"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-category">Categoría</Label>
              <Input
                id="edit-category"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value })
                }
                className="bg-secondary/50"
                data-testid="edit-service-category"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-active">Activo</Label>
              <Switch
                id="edit-active"
                checked={form.is_active}
                onCheckedChange={(checked) =>
                  setForm({ ...form, is_active: checked })
                }
                data-testid="edit-service-active"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-edit-service"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
